import clsx from 'clsx'
import { motion, useReducedMotion } from 'motion/react'
import { useState } from 'react'
import { type Story, StoryVisibility } from '@/@types/story'
import BubbleIcon from '@/assets/bubble.svg?react'
import GiftIcon from '@/assets/gift.svg?react'
import LikeIcon from '@/assets/like.svg?react'
import ActionButton from '@/components/ActionButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '@/components/BlurTextSwitch'
import styles from './StoryActions.module.css'

export interface StoryActionsProps {
	story: Story
	onLike: () => void
	onComment: () => void
}

export default function StoryActions({ story, onLike, onComment }: StoryActionsProps) {
	const prefersReducedMotion = useReducedMotion()
	const [likeFeedbackKey, setLikeFeedbackKey] = useState(0)
	const likeLabel = story.likeCount > 0 ? String(story.likeCount) : '点赞'
	const giftLabel = story.endowCount > 0 ? String(story.endowCount) : '赠送'
	const commentLabel = story.commentCount > 0 ? String(story.commentCount) : '留言'
	const commentActionLabel = story.status === StoryVisibility.EXPIRED ? '查看留言' : '留言'

	const handleLike = () => {
		if (!story.liked) {
			setLikeFeedbackKey((key) => key + 1)
		}

		onLike()
	}

	return (
		<div className={styles.actions}>
			<ActionButton
				className={clsx(styles.action, story.liked && styles.likedAction)}
				tooltip={story.liked ? '取消点赞' : '点赞'}
				aria-label={`${story.liked ? '取消点赞' : '点赞'}，${likeLabel}`}
				aria-pressed={story.liked}
				onClick={handleLike}
			>
				<motion.span
					key={likeFeedbackKey}
					className={styles.actionIcon}
					initial={prefersReducedMotion ? false : { scale: 0.72, rotate: -12 }}
					animate={{ scale: 1, rotate: 0 }}
					transition={{ type: 'spring', stiffness: 520, damping: 18 }}
				>
					<LikeIcon />
				</motion.span>
				<FeaturableBlurTextSwitch data={likeLabel} mode={BlurTextMode.SMALL} />
			</ActionButton>
			<div className={styles.readOnlyAction} role="group" aria-label={`赠送，${giftLabel}`}>
				<GiftIcon aria-hidden="true" />
				<FeaturableBlurTextSwitch data={giftLabel} mode={BlurTextMode.SMALL} />
			</div>
			<ActionButton
				className={styles.action}
				tooltip={commentActionLabel}
				aria-label={`${commentActionLabel}，${commentLabel}`}
				onClick={onComment}
			>
				<BubbleIcon />
				<FeaturableBlurTextSwitch data={commentLabel} mode={BlurTextMode.SMALL} />
			</ActionButton>
		</div>
	)
}
